import { Form, Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useIntl } from 'react-intl';
import Config from '../../config/Config';
import '../../css/DppForm.css';

// Validation schema
const schema = z
  .object({
    type: z.enum(['Installation', 'Removal'], {
      required_error: 'Type is required',
    }),
    BatteryVoltage: z
      .string()
      .optional()
      .transform((val) => (val === '' ? undefined : val))
      .refine(
        (val) => val === undefined || (!isNaN(Number(val)) && Number(val) > 0),
        { message: 'Bitte geben Sie eine positive Zahl ein' }
      ),
    MeterReading: z
      .string()
      .optional()
      .transform((val) => (val === '' ? undefined : val))
      .refine(
        (val) => val === undefined || (!isNaN(Number(val)) && Number(val) > 0),
        { message: 'Bitte geben Sie eine positive Zahl ein' }
      ),
    Date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Format: YYYY-MM-DD'),
  })
  .refine(
    (data) => data.BatteryVoltage !== undefined || data.MeterReading !== undefined, {}
  );


const WaterMeterReadingsForm = ({
  initialType = 'Installation',
  showButtons = true,
  onSave,
  onCancel,
  isModal = false,
}) => {
  const intl = useIntl();
  const isDemo = Config.isDemo === 'true';
  const noAccessMessage = intl.formatMessage({ id: 'no_access_message', defaultMessage: 'Not authorized for this action' });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      type: initialType,
      BatteryVoltage: '',
      MeterReading: '',
      Date: new Date().toISOString().split('T')[0],
    },
    mode: 'onTouched',
  });

  const selectedType = watch('type');

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const handleSave = (data) => {
    // Transform data if needed for backend
    const cleanedData = {
      ...data,
      BatteryVoltage: data.BatteryVoltage ? Number(data.BatteryVoltage) : undefined,
      MeterReading: data.MeterReading ? Number(data.MeterReading) : undefined,
    };
    onSave?.(cleanedData);
    if (isModal) reset();
  };

  return (
    <Form 
    onSubmit={handleSubmit(handleSave)} 
    noValidate 
    className="dpp-form"
    >
      <Form.Group className="mb-4" controlId="type">
        <Form.Label>{intl.formatMessage({ id: 'type_of_report' })}</Form.Label>
        <div className="dpp-radio-group">
          {[
            {
              id: 'installation',
              value: 'Installation',
              title: intl.formatMessage({ id: 'installation' }),
              desc: intl.formatMessage({ id: 'installation_description' }),
              icon: '⚙️',
            },
            {
              id: 'removal',
              value: 'Removal',
              title: intl.formatMessage({ id: 'demontage' }),
              desc: intl.formatMessage({ id: 'demontage_description' }),
              icon: '🧰',
            },
          ].map((opt) => (
            <label
              key={opt.id}
              htmlFor={opt.id}
              className={`dpp-radio-card ${
                selectedType === opt.value ? 'selected' : ''
              }`}
            >
              <input
                type="radio"
                id={opt.id}
                value={opt.value}
                {...register('type')}
              />
              <div className="card-content">
                <div className="icon">{opt.icon}</div>
                <div className="text">
                  <h5>{opt.title}</h5>
                  <p>{opt.desc}</p>
                </div>
              </div>
            </label>
          ))}
        </div>
        {errors.type && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {errors.type.message}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {/* Fields (Side by Side on lg and up) */}
      <Row>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="BatteryVoltage">
            <Form.Label>{intl.formatMessage({ id: 'battery_voltage' })}</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              placeholder=""
              {...register('BatteryVoltage')}
              isInvalid={!!errors.BatteryVoltage}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.BatteryVoltage?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-4" controlId="MeterReading">
            <Form.Label>{intl.formatMessage({ id: 'meter_reading' })}</Form.Label>
            <Form.Control
              type="number"
              placeholder=""
              {...register('MeterReading')}
              isInvalid={!!errors.MeterReading}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.MeterReading?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col lg={6} className="mb-4">
          <Form.Group controlId="Date">
            <Form.Label>{intl.formatMessage({ id: 'date' })}</Form.Label>
            <Form.Control
              type="date"
              {...register('Date')}
              isInvalid={!!errors.Date}
              className="dpp-form-control"
            />
            <Form.Control.Feedback type="invalid">
              {errors.Date?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Save / Cancel Buttons */}
      {showButtons && (
        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="outline-secondary"
            onClick={handleCancel}
            className="dpp-modal-button"
          >
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          {isDemo ? (
            <OverlayTrigger
              placement="auto"
              overlay={<Tooltip id="save-tooltip">{noAccessMessage}</Tooltip>}
            >
              <span className="d-inline-block">
                <Button
                  variant="primary"
                  type="submit"
                  disabled
                  className="dpp-modal-button"
                  style={{ pointerEvents: 'none' }}
                >
                  {intl.formatMessage({ id: 'save' })}
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="primary"
              type="submit"
              disabled={!isDirty || !isValid}
              className="dpp-modal-button"
            >
              {intl.formatMessage({ id: 'save' })}
            </Button>
          )}
        </div>
      )}
    </Form>
  );
};

export default WaterMeterReadingsForm;